import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { specialtyPhysicianInfoService } from '../../../../services/specialtyPhysicianInfo';
import { Alert, ActivityIndicator } from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('SpecialtyPhysicianInput');

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

type SpecialtyPhysicianInputScreenRouteProp = RouteProp<Record<string, { specialtyPhysicianData?: any; isEditing?: boolean }>, string>;

export const SpecialtyPhysicianInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<SpecialtyPhysicianInputScreenRouteProp>();
  const [showStateModal, setShowStateModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);

  const { specialtyPhysicianData, isEditing } = route.params || {};

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const [formData, setFormData] = useState({
    fullName: specialtyPhysicianData?.fullName || '',
    specialty: specialtyPhysicianData?.specialty || '',
    phoneNumber: specialtyPhysicianData?.phoneNumber || '',
    email: specialtyPhysicianData?.email || '',
    officeAddress: specialtyPhysicianData?.officeAddress || '',
    city: specialtyPhysicianData?.city || '',
    state: specialtyPhysicianData?.state || '',
    username: '',
    password: '',
    notes: specialtyPhysicianData?.notes || '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEditing && specialtyPhysicianData) {
      setFormData(prev => ({
        ...prev,
        fullName: specialtyPhysicianData.fullName || '',
        specialty: specialtyPhysicianData.specialty || '',
        phoneNumber: formatPhoneNumber(specialtyPhysicianData.phoneNumber || ''),
        email: specialtyPhysicianData.email || '',
        officeAddress: specialtyPhysicianData.officeAddress || '',
        city: specialtyPhysicianData.city || '',
        state: specialtyPhysicianData.state || '',
        notes: specialtyPhysicianData.notes || '',
      }));

      // Try to load portal credentials if we have an ID
      if (specialtyPhysicianData.id) {
        (async () => {
          try {
            const creds = await vaultService.getPortalCredentials(specialtyPhysicianData.id, 'specialty_physician');
            if (creds && (creds.username || creds.password)) {
              setHasOnlinePortal(true);
              setFormData(prev => ({
                ...prev,
                username: creds.username || '',
                password: creds.password || '',
              }));
            }
          } catch (error) {
            logger.debug('Failed to load portal credentials:', error);
          }
        })();
      }
    }
  }, [isEditing, specialtyPhysicianData]);

  const handleSubmit = async () => {
    // Validation - customize based on required fields
    if (!formData.fullName.trim()) {
      Alert.alert('Error', 'Please enter the physician\'s name');
      return;
    }

    if (!formData.specialty.trim()) {
      Alert.alert('Error', 'Please enter the physician\'s specialty');
      return;
    }

    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }

      const specialtyPhysicianUpdateData = {
        taskId: 7, // Specialty Physician task ID
        fullName: formData.fullName.trim(),
        specialty: formData.specialty.trim(),
        phoneNumber: formData.phoneNumber.replace(/\D/g, ''),
        email: formData.email.trim() || undefined,
        officeAddress: formData.officeAddress.trim() || undefined,
        city: formData.city.trim() || undefined,
        state: formData.state || undefined,
        notes: formData.notes.trim() || undefined,
      };

      let savedSpecialtyPhysician;

      if (isEditing && specialtyPhysicianData?.id) {
        // Update existing record
        savedSpecialtyPhysician = await specialtyPhysicianInfoService.updateSpecialtyPhysicianInfo(
          specialtyPhysicianData.id,
          specialtyPhysicianUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedSpecialtyPhysician?.data || savedSpecialtyPhysician;
        logger.debug('SpecialtyPhysician updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        (navigation as any).navigate('SpecialtyPhysicianLockUnlock', { specialtyPhysicianId: updatedData.id });
      } else {
        // Create new record
        savedSpecialtyPhysician = await specialtyPhysicianInfoService.createSpecialtyPhysicianInfo(specialtyPhysicianUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedSpecialtyPhysician?.data || savedSpecialtyPhysician;
        logger.debug('SpecialtyPhysician saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        (navigation as any).navigate('SpecialtyPhysicianLockUnlock', { specialtyPhysicianId: createdData.id });
      }

      // Handle wrapped response format for vault storage
      const savedData = savedSpecialtyPhysician?.data || savedSpecialtyPhysician;
      // Store portal credentials in Vault if provided
      if (savedData.id && (formData.username || formData.password)) {
        try {
          logger.debug('[Portal Debug] Storing portal credentials in vault for specialty physician ID:', savedData.id);
          await vaultService.storePortalCredentials(
            savedData.id,
            'specialty_physician',
            formData.username,
            formData.password
          );
        } catch (vaultError) {
          logger.debug('[Portal Debug] Failed to store credentials in vault:', vaultError);
        }
      }

    } catch (error) {
      logger.error('Error saving specialty physician:', error);
      Alert.alert('Error', 'Failed to save specialty physician. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStateSelect = (state: string) => {
    setFormData({ ...formData, state });
    setShowStateModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Specialty Physician Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Physician Name:</Text>
            <TextInput
              style={styles.input}
              value={formData.fullName}
              onChangeText={(text) => setFormData({ ...formData, fullName: text })}
              placeholder="Enter physician's full name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Specialty:</Text>
            <TextInput
              style={styles.input}
              value={formData.specialty}
              onChangeText={(text) => setFormData({ ...formData, specialty: text })}
              placeholder="Enter specialty (e.g., Cardiologist, Dermatologist)"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number:</Text>
            <TextInput
              style={styles.input}
              value={formData.phoneNumber}
              onChangeText={(text) => setFormData({ ...formData, phoneNumber: formatPhoneNumber(text) })}
              placeholder="Enter phone number"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email:</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={(text) => setFormData({ ...formData, email: text })}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Office Address:</Text>
            <TextInput
              style={styles.input}
              value={formData.officeAddress}
              onChangeText={(text) => setFormData({ ...formData, officeAddress: text })}
              placeholder="Enter street address"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>City:</Text>
            <TextInput
              style={styles.input}
              value={formData.city}
              onChangeText={(text) => setFormData({ ...formData, city: text })}
              placeholder="Enter city"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>State:</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowStateModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.state && { color: '#A9A9A9' }
              ]}>
                {formData.state || 'Select State'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does this physician have an online patient portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter any special instructions or notes"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, submitting && { opacity: 0.7 }]}
              onPress={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <Modal
        visible={showStateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStateModal(false)}
        statusBarTranslucent
        hardwareAccelerated={true} // ← ADDED THIS
      >
        <View style={styles.modalOverlay}>
          {/* This handles the transparent dimmed background and closes the modal */}
          <TouchableOpacity
            style={{ flex: 1 }}
            activeOpacity={1}
            onPress={() => setShowStateModal(false)}
          />

          {/* This is the actual modal content that should stay visible */}
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowStateModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(state)}
                >
                  <Text style={styles.stateItemText}>{state}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
        borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  submitButtonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});
